/**
 * Carries what a visitor chose on an AI interior design landing page (their
 * room photo or a sample, the mode, room type, style or idea) through sign-up
 * to the AI Interior Designer. It waits in this browser's IndexedDB (a photo
 * can't ride along in a URL or survive the OAuth redirect in memory) and is
 * taken out once, by /ai-interior-designer, which opens with it filled in.
 *
 * Never throws: when storage is unavailable (private windows, quota), the
 * visitor simply picks their photo again in the tool.
 */
import type { InteriorMode } from '@/constants/interiorDesigner';

const DB_NAME = 'ep-interior-handoff';
const STORE = 'handoff';
const KEY = 'pending';
/** A hand-off older than this is dropped instead of applied. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type InteriorHandoffPhoto =
  | { kind: 'file'; file: File }
  /** A public image (one of the landing page's sample rooms). */
  | { kind: 'url'; url: string; label?: string };

export interface InteriorHandoff {
  /** Landing page the visitor started from (analytics). */
  source: string;
  mode?: InteriorMode;
  /** Room type value the tool sends to the API (English). */
  roomType?: string;
  /** Style (theme name) to preselect. */
  style?: string;
  /** Text for the prompt field (an idea, a wall color). */
  prompt?: string;
  photo?: InteriorHandoffPhoto;
  savedAt: number;
}

const openDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const run = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = action(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
};

/** Saves the hand-off; returns false when it couldn't be stored. */
export async function saveInteriorHandoff(
  value: Omit<InteriorHandoff, 'savedAt'>,
): Promise<boolean> {
  const record: InteriorHandoff = { ...value, savedAt: Date.now() };
  try {
    await run('readwrite', (store) => store.put(record, KEY));
    return true;
  } catch {
    return false;
  }
}

const isText = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max;

/** Returns the saved hand-off (if fresh) and removes it, so it's applied once. */
export async function takeInteriorHandoff(): Promise<InteriorHandoff | null> {
  try {
    const record = await run<InteriorHandoff | undefined>(
      'readonly',
      (store) => store.get(KEY) as IDBRequest<InteriorHandoff | undefined>,
    );
    if (!record) return null;
    await run('readwrite', (store) => store.delete(KEY));
    if (typeof record.savedAt !== 'number') return null;
    if (Date.now() - record.savedAt > MAX_AGE_MS) return null;

    const photo = record.photo;
    const validPhoto =
      !photo ||
      (photo.kind === 'file' && photo.file instanceof Blob) ||
      (photo.kind === 'url' &&
        isText(photo.url, 2000) &&
        /^https:\/\//.test(photo.url));

    return {
      source: isText(record.source, 120) ? record.source : 'landing',
      mode: record.mode,
      roomType: isText(record.roomType, 80) ? record.roomType : undefined,
      style: isText(record.style, 80) ? record.style : undefined,
      prompt: isText(record.prompt, 1000) ? record.prompt : undefined,
      photo: validPhoto ? photo : undefined,
      savedAt: record.savedAt,
    };
  } catch {
    return null;
  }
}
